/**
 * MasterDataUpdatesFormDialog — Create/Edit `updates` (premium history).
 * 1:1 port of Access `frmMdQryUpdatesAdd`. Compact Sales Contract style.
 *
 * Single-column, field order by Top: Date → Code (combo list_and_valo) →
 * Premium vs G2 5Percent (Standard) → Reason of Change (multiline).
 * Composite PK (date + code): required, disable cả 2 khi edit. VBA: date/code
 * trống → cảnh báo; (date,code) trùng (create) → "Date - Code is existed!" (BE raise).
 * `description` là cột JOIN từ list_and_valo — hiển thị readonly khi chọn code,
 * KHÔNG lưu qua form (đúng Access Add form).
 */
import {
  Autocomplete,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'
import { useEffect, useMemo, useState } from 'react'

import { useSnackbar } from '@/hooks/useSnackbar'
import { useListQualitiesQuery } from '@/store/api/lookupsApi'
import {
  useCreateMdUpdateMutation,
  useUpdateMdUpdateMutation,
} from '@/store/api/masterDataCrudApi'
import type { LookupQuality, MasterDataUpdate } from '@/types/api'
import { getErrorMessage } from '@/utils/error'

import { compactDialogContentSx, dialogHeaderBtnSx, dialogTitleActionsSx, dialogTitleSx, dialogTitleTextSx, numSx, roSx } from '../purchase/dialogStyle'

export interface Props {
  open: boolean
  mode: 'create' | 'edit'
  initialValue?: MasterDataUpdate | null
  onClose: () => void
  onSaved?: () => void
}

type FormState = {
  date: string
  code: string
  description: string
  premium_vs_g2_5percent: string
  reason_of_change: string
}

const EMPTY: FormState = {
  date: '',
  code: '',
  description: '',
  premium_vs_g2_5percent: '',
  reason_of_change: '',
}

const parseNumeric = (s: string): number | null => {
  const t = (s ?? '').replace(/,/g, '').trim()
  if (t === '') return null
  const n = Number(t)
  return Number.isNaN(n) ? null : n
}
const formatStandard = (v: string | number | null | undefined): string => {
  if (v == null || v === '') return ''
  const n = typeof v === 'number' ? v : Number(String(v).replace(/,/g, ''))
  if (Number.isNaN(n)) return String(v)
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

export default function MasterDataUpdatesFormDialog({
  open,
  mode,
  initialValue,
  onClose,
  onSaved,
}: Props) {
  const snack = useSnackbar()
  const isEdit = mode === 'edit'

  const [createUpdate, { isLoading: creating }] = useCreateMdUpdateMutation()
  const [updateUpdate, { isLoading: updating }] = useUpdateMdUpdateMutation()
  const loading = creating || updating

  const { data: qualities = [] } = useListQualitiesQuery({ local_only: false })

  const [form, setForm] = useState<FormState>(EMPTY)
  const [showErrors, setShowErrors] = useState(false)

  useEffect(() => {
    if (!open) return
    setShowErrors(false)
    if (isEdit && initialValue) {
      setForm({
        date: initialValue.date ?? '',
        code: initialValue.code ?? '',
        description: initialValue.description ?? '',
        premium_vs_g2_5percent: formatStandard(initialValue.premium_vs_g2_5percent),
        reason_of_change: initialValue.reason_of_change ?? '',
      })
    } else {
      // New: auto-load hôm nay vào Date (Access default = Now).
      setForm({ ...EMPTY, date: dayjs().format('YYYY-MM-DD') })
    }
  }, [open, isEdit, initialValue])

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) =>
    setForm((p) => ({ ...p, [k]: v }))

  // Code combo (list_and_valo) — inject selected nếu chưa có trong options.
  const selectedQuality = useMemo<LookupQuality | null>(() => {
    if (!form.code) return null
    return (
      qualities.find((v) => v.code === form.code) ??
      ({ code: form.code, description: form.description } as LookupQuality)
    )
  }, [qualities, form.code, form.description])
  const qualityOpts = useMemo(
    () =>
      selectedQuality && !qualities.some((o) => o.code === selectedQuality.code)
        ? [selectedQuality, ...qualities]
        : qualities,
    [qualities, selectedQuality],
  )

  const dateEmpty = !form.date
  const codeEmpty = !form.code.trim()
  const canSave = !dateEmpty && !codeEmpty && !loading

  const handleClose = () => {
    if (loading) return
    onClose()
  }

  const handleSave = async () => {
    if (dateEmpty || codeEmpty) {
      setShowErrors(true)
      return
    }
    try {
      if (isEdit && initialValue) {
        await updateUpdate({
          date: initialValue.date,
          code: initialValue.code,
          body: {
            premium_vs_g2_5percent: parseNumeric(form.premium_vs_g2_5percent),
            reason_of_change: form.reason_of_change.trim() || null,
          },
        }).unwrap()
        snack.success('Update saved')
      } else {
        await createUpdate({
          date: form.date,
          code: form.code.trim(),
          premium_vs_g2_5percent: parseNumeric(form.premium_vs_g2_5percent),
          reason_of_change: form.reason_of_change.trim() || null,
        }).unwrap()
        snack.success('Update created')
      }
      onSaved?.()
      onClose()
    } catch (e) {
      snack.error(getErrorMessage(e, isEdit ? 'Failed to update' : 'Failed to create'))
    }
  }

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        if (loading) return
        if (reason === 'backdropClick') return
        handleClose()
      }}
      maxWidth="sm"
      fullWidth
      scroll="paper"
    >
      <DialogTitle sx={dialogTitleSx}>
        <Typography component="span" noWrap sx={dialogTitleTextSx}>
          {isEdit ? 'Edit Update' : 'New Update'}
          {isEdit && initialValue ? (
            <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
              {initialValue.date} / {initialValue.code}
            </Typography>
          ) : null}
        </Typography>
        <Stack direction="row" spacing={0.5} sx={dialogTitleActionsSx}>
          <Button
            type="button"
            variant="contained"
            size="small"
            color="success"
            onClick={() => void handleSave()}
            disabled={!canSave}
            sx={dialogHeaderBtnSx}
          >
            {loading ? <CircularProgress size={14} color="inherit" /> : 'Save'}
          </Button>
          <Button type="button" variant="outlined" size="small" onClick={handleClose} disabled={loading} sx={dialogHeaderBtnSx}>
            Cancel
          </Button>
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent dividers sx={compactDialogContentSx}>
        <Grid container spacing={1.5} sx={{ mt: 0 }}>
          <Grid item xs={12}>
            <DatePicker
              label="Date"
              format="DD-MMM-YYYY"
              disabled={isEdit}
              value={form.date ? dayjs(form.date) : null}
              onChange={(d) => set('date', d && d.isValid() ? d.format('YYYY-MM-DD') : '')}
              slotProps={{
                textField: {
                  size: 'small',
                  fullWidth: true,
                  required: true,
                  error: showErrors && dateEmpty,
                  InputLabelProps: { shrink: true },
                  inputProps: { tabIndex: isEdit ? -1 : 1 },
                  sx: isEdit ? roSx : undefined,
                },
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <Autocomplete
              size="small"
              fullWidth
              disabled={isEdit}
              options={qualityOpts}
              value={selectedQuality}
              onChange={(_e, v) => {
                set('code', v?.code ?? '')
                set('description', v?.description ?? '')
              }}
              getOptionLabel={(o) => (o ? `${o.code} — ${o.description ?? ''}` : '')}
              isOptionEqualToValue={(a, b) => a.code === b.code}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Code"
                  required
                  error={showErrors && codeEmpty}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ ...params.inputProps, tabIndex: isEdit ? -1 : 2 }}
                  sx={isEdit ? roSx : undefined}
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Description"
              size="small"
              fullWidth
              multiline
              minRows={2}
              value={form.description}
              InputLabelProps={{ shrink: true }}
              InputProps={{ readOnly: true }}
              inputProps={{ tabIndex: -1 }}
              sx={roSx}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Premium vs. G2 5Percent"
              size="small"
              fullWidth
              value={form.premium_vs_g2_5percent}
              onChange={(e) => set('premium_vs_g2_5percent', e.target.value.replace(/[^0-9.,-]/g, ''))}
              onBlur={() => set('premium_vs_g2_5percent', formatStandard(form.premium_vs_g2_5percent))}
              InputLabelProps={{ shrink: true }}
              inputProps={{ tabIndex: 3, inputMode: 'decimal', style: { textAlign: 'right' } }}
              sx={numSx}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Reason of Change"
              size="small"
              fullWidth
              multiline
              minRows={3}
              value={form.reason_of_change}
              onChange={(e) => set('reason_of_change', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ tabIndex: 4 }}
            />
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  )
}
